import axios from 'axios';
import FuseUtils from '@fuse/FuseUtils';
import { API_BASE_URL } from '@config';
import store from 'app/store';
import { showMessage } from 'app/store/actions/fuse';

class jwtService extends FuseUtils.EventEmitter {
	init() {
		this.setInterceptors();
		this.handleAuthentication();
		this.listenForTokenChanges();
	}

	/**
	 * Listen for token changes in other tabs.
	 * When another tab refreshes the token, localStorage is updated.
	 * This tab picks up the new token so it doesn't get 401 with the old one.
	 */
	listenForTokenChanges = () => {
		window.addEventListener('storage', (event) => {
			if (event.key === 'token') {
				if (event.newValue) {
					// Another tab updated the token - adopt it
					axios.defaults.headers.common['Authorization'] = `Bearer ${event.newValue}`;
				} else {
					// Another tab logged out - follow suit
					delete axios.defaults.headers.common['Authorization'];
					this.emit('onAutoLogout', 'Logged out from another tab');
				}
			}
		});
	}

	setInterceptors = () => {
		axios.interceptors.request.use(
			config => config,
			error => {
				Promise.reject(error);
			}
		);

		axios.interceptors.response.use(
			response => response,
			err => {
				const { response, config } = err;
				if (!response) return Promise.reject(err);
				const { status } = response;
				const { __isRetryRequest, url } = config;
				if (url && url.includes('/api/auth/ldap/login')) {
					return Promise.reject(err);
				}

				// Generic permission-denied toast for any 403 response. Works
				// for license-restricted trials, role-denied actions, owner
				// mismatches, etc. Keeps the handler out of every call site.
				// Callers can opt out by setting `skipForbiddenToast: true` on
				// the request config - useful for endpoints that gracefully
				// degrade (e.g. participant role legitimately lacks access and
				// the consumer falls back to defaults).
				//
				// AI endpoints (/api/ai/* and /api/ai-assistant/*) are also
				// silenced: a 403 with "AI features are not enabled" there is
				// a normal disabled-state response, not a permission error.
				// Heidi instances without AI configured (most installs)
				// otherwise get a "AI features are not enabled" snackbar on
				// the Cluster Manager page every time auto-failure-analysis
				// or the AI assistant status check fires - pure noise.
				const isAiUrl = url && (
					url.includes('/api/ai/')
					|| url.includes('/api/ai-assistant/')
					|| url.endsWith('/api/ai')
					|| url.endsWith('/api/ai-assistant')
				);
				// Heidi Storage answers 403 STORAGE_DISABLED (and 404 STORAGE_UNAVAILABLE)
				// on a deployment where the feature is not switched on. That is a
				// SETTING, not a permission error - the Storage pages stay in the menu
				// deliberately, so an administrator discovers the feature, and each
				// page explains what to do about it. A red toast on top of that
				// explanation reads as a fault and is pure noise. Same reasoning as
				// the AI endpoints above, keyed on the code rather than the URL so a
				// genuine storage permission denial still surfaces.
				const forbiddenData = (response && response.data) || {};
				const isStorageDisabled = forbiddenData
					&& (forbiddenData.code === 'STORAGE_DISABLED' || forbiddenData.code === 'STORAGE_UNAVAILABLE');
				if (status === 403 && !(config && config.skipForbiddenToast) && !isAiUrl && !isStorageDisabled) {
					const data = response.data || {};
					const apiError = typeof data === 'string' ? data : (data.error || data.message);
					const isLicenseRestricted = data.code === 'LICENSE_RESTRICTED';
					store.dispatch(showMessage({
						message: apiError
							|| (isLicenseRestricted
								? 'This action is not available on your current plan'
								: 'You do not have permission to perform this action'),
						variant: isLicenseRestricted ? 'warning' : 'error',
						autoHideDuration: 6000,
					}));
				}

				return new Promise((resolve, reject) => {
					if (status === 401 && config && !__isRetryRequest && !url.includes('/api/auth/login')) {
						// Only trigger auto-logout if not the verify-code endpoint
						if (!url.includes('/api/two-step-verification/verify-code')) {
							// Check if another tab has refreshed the token since this request was sent.
							// If localStorage has a newer token, retry with it instead of logging out.
							const failedToken = config.headers && config.headers.Authorization
								? config.headers.Authorization.replace('Bearer ', '')
								: null;
							const currentToken = this.getAccessToken();

							if (currentToken && failedToken && currentToken !== failedToken) {
								// Token was refreshed by another tab - retry the request with the new token
								config.headers.Authorization = `Bearer ${currentToken}`;
								config.__isRetryRequest = true;
								return resolve(axios(config));
							}

							this.emit('onAutoLogout', undefined, failedToken);
							this.setSession(null);
						}
					}
					reject(response);
				});
			}
		);
	};

	handleAuthentication = () => {
		// Check for portal SSO token in URL
		var urlParams = new URLSearchParams(window.location.search);
		var ssoToken = urlParams.get('sso_token');
		if (ssoToken) {
			// Clean the URL (remove sso_token param)
			var cleanUrl = window.location.pathname;
			window.history.replaceState({}, document.title, cleanUrl);
			// Set session with SSO token and trigger auto-login
			this.setSession(ssoToken);
			this.emit('onAutoLogin', true);
			return;
		}

		let token = this.getAccessToken();
		if (!token) {
			this.emit('onNoAccessToken');
			return;
		}
		if (token) {
			this.setSession(token);
			this.emit('onAutoLogin', true);
		} else {
			this.emit('onAutoLogout', 'Session Expired');
		}
	};

	createUser = data => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${API_BASE_URL}/api/auth/register`, data)
				.then(res => {
					if (res.data.licenseType === 'Site') {
						this.setSession(res.data.token);
						resolve(res.data.token);
					} else {
						this.logout();
						resolve();
					}
				})
				.catch(err => {
					if (err.status === 406 && err.data.reason === 'duplicateEntry') {
						reject(err);
					} else {
						this.emit('createUserError', err.data.message);
						reject(err);
					}
				});
		});
	};

	createUserByAdmin = data => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${API_BASE_URL}/api/auth/register`, data)
				.then(res => {
					resolve(res.data.token);
				})
				.catch(err => {
					if (err.status === 406 && err.data.reason === 'duplicateEntry') {
						reject(err);
					} else {
						this.emit('createUserError', err.data.message);
						reject(err);
					}
				});
		});
	};

	signInForLocal = data => {
		return new Promise((resolve, reject) => {
			axios
				// The login form shows blocking 403 errors (suspended org,
				// expired license) in its own dialog - skip the generic toast.
				.post(`${API_BASE_URL}/api/auth/login`, data, { skipForbiddenToast: true })
				.then(response => {
					if (response.data) {
						this.setSession(response.data.token);
						resolve(response.data.token);
					} else {
						reject(response.data.error);
					}
				})
				.catch(err => reject(err));
		});
	};

	signInForLdap = data => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${API_BASE_URL}/api/auth/ldap/login`, data)
				.then(response => {
					if (response.data) {
						this.setSession(response.data.token);
						resolve(response.data.token);
					} else {
						reject(response.data.error);
					}
				})
				.catch(err => reject(err));
		});
	};

	signInForTrainingSession = (fullName, sessionCode, participantEmail) => {
		const payload = {
			fullName,
			sessionCode,
			participantEmail
		};
		return new Promise((resolve, reject) => {
			axios
				.post(`${API_BASE_URL}/api/auth/training/register`, payload)
				.then(response => {
					if (response.data) {
						this.setSession(response.data.token);
						resolve(response.data.token);
					} else {
						reject(response.data.error);
					}
				})
				.catch(err => reject(err));
		});
	}
	signInForOidc = ({ code, codeVerifier, configId, state, expectedState, provider }) => {
		return new Promise((resolve, reject) => {
			if (state !== expectedState) {
				return reject({ message: 'State mismatch.' });
			}

			axios
				.post(`${API_BASE_URL}/api/openid/auth/${provider}/callback`, {
					code,
					state,
					codeVerifier,
					configId,
					expectedState
				})
				.then(res => {
					if (res.data.token) {
						this.setSession(res.data.token);
						resolve(res.data.token);
					} else {
						reject({ message: 'Missing token in response.' });
					}
				})
				.catch(err => reject(err));
		});
	};

	signInWithToken = () => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${API_BASE_URL}/api/auth/refresh`, null, {
					headers: {
						repeatCall: true
					}
				})
				.then(res => {
					if (res.data.token) {
						this.setSession(res.data.token);
						resolve(res.data.token);
					} else {
						this.emit('onAutoLogout', 'Session Expired');
						reject('Unauthorized');
					}
				})
				.catch(error => {
					this.emit('onAutoLogout', 'Session Expired');
					reject(error?.data?.message);
				});
		});
	};

	setSession = token => {
		if (token) {
			window.localStorage.setItem('token', token);
			axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
		} else {
			[
				'token',
				'N-page',
				'Q-page',
				'Job-page',
				'Cred-page',
				'File-page',
				'Cluster-tab',
				'Cluster-page',
				'N-rowsPerPage',
				'Q-rowsPerPage',
				'Job-rowsPerPage',
				'Cred-rowsPerPage',
				'File-rowsPerPage',
				'Cluster-rowsPerPage'
				// Note: We don't clear secure login credentials on logout
				// Users must explicitly clear them using the "Clear" button
			].forEach((item) => window.localStorage.removeItem(item));

			delete axios.defaults.headers.common['Authorization'];
		}
	};

	logout = () => {
		return this.setSession(null);
	};

	getAccessToken = () => window.localStorage.getItem('token');

	updateUserData = user => axios.put('/api/auth/update', { user });
}

const instance = new jwtService();

export default instance;
